import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  getPatchReceptionCallHistoryMutationKey,
  usePatchReceptionCallHistory,
} from '@reception-web/common/api/app'
import {
  HISTORY_CALLER_STATUS,
  HISTORY_RECEIVER_STATUS,
} from '@reception-web/common/consts'
import { useAudio } from '@reception-web/common/hooks'
import { Button, Card, Col, Modal, Row, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { MdOutlineVideocam } from 'react-icons/md'
import styled from 'styled-components'

const { Title } = Typography
import {
  useAosToken,
  useAttendeePresenceListener,
  useAudioEnabled,
  useCallStatus,
  useMeeting,
  useMeetingContext,
  useMessaging,
  useReservation,
} from '../../hooks'
import { ChatComponent } from '../message'

import { AutoLeaveCountdown } from './AutoLeaveCountdown'
import { MeetingInfo } from './MeetingInfo'
import { VideoContainer } from './VideoContainer'
import { VolumeIcon } from './VolumeIcon'

const Container = styled(Card)`
  width: 80%;
`

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const Waiting = styled.div`
  width: 100%;
  text-align: center;
  height: 100%;
  font-size: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Calling = styled.div`
  width: 60%;
  margin: 0 auto;
  text-align: center;
  font-size: 28px;
`

const AUTO_LEAVE_TIMEOUT_MILLISECONDS = 1000 * 60 * 3

export const MeetingComponent: React.FC = () => {
  const { leaveMeeting } = useMeeting()
  const { accept, reservation } = useReservation()
  const { information, meeting, attendee, isRedial } = useMeetingContext()
  const autoLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isAutoLeaveModalOpen, setIsAutoLeaveModalOpen] = useState(false)
  const [isAutoLeaveCountdownActive, setIsAutoLeaveCountdownActive] =
    useState(false)
  const aosToken = useAosToken()
  const { trigger: patchReceptionCallHistory } = usePatchReceptionCallHistory(
    meeting?.MeetingId ?? reservation?.task.attributes.Meeting.MeetingId ?? '',
    {
      swr: {
        swrKey: getPatchReceptionCallHistoryMutationKey(
          meeting?.MeetingId ??
            reservation?.task.attributes.Meeting.MeetingId ??
            ''
        )[0],
      },
      axios: {
        headers: {
          Authorization: `Token ${aosToken}`,
        },
      },
    }
  )

  const { messages, message, setMessage, sendMessage } = useMessaging()

  const { t } = useTranslation('translation', {
    keyPrefix: 'page.meeting',
  })

  const { play: playRingAudio, stop: stopRingAudio } = useAudio({
    source: '/ring.mp3',
  })
  const { play: playCallbackAudio, stop: stopCallbackAudio } = useAudio({
    source: '/callback.mp3',
  })
  const { audioEnabled, setAudioEnabled } = useAudioEnabled()

  const { status } = useCallStatus()

  const clearAutoLeaveTimer = useCallback(() => {
    if (autoLeaveTimerRef.current) {
      clearTimeout(autoLeaveTimerRef.current)
      autoLeaveTimerRef.current = null
    }
    setIsAutoLeaveCountdownActive(false)
  }, [])

  const autoLeave = useCallback(async () => {
    clearAutoLeaveTimer()

    try {
      if (isRedial) {
        await patchReceptionCallHistory({
          caller_status: HISTORY_CALLER_STATUS.THE_CALL_FINISHED_NORMALLY,
          finished: new Date().toISOString(),
        })
      } else {
        await patchReceptionCallHistory({
          receiver_status: HISTORY_RECEIVER_STATUS.THE_CALL_FINISHED_NORMALLY,
          finished: new Date().toISOString(),
        })
      }
    } finally {
      await leaveMeeting({ reload: false })
      setIsAutoLeaveModalOpen(true)
    }
  }, [clearAutoLeaveTimer, isRedial, leaveMeeting, patchReceptionCallHistory])

  const handleGuestLeave = useCallback(() => {
    if (status !== 'calling') {
      return
    }

    clearAutoLeaveTimer()
    setIsAutoLeaveCountdownActive(true)
    autoLeaveTimerRef.current = setTimeout(() => {
      void autoLeave()
    }, AUTO_LEAVE_TIMEOUT_MILLISECONDS)
  }, [autoLeave, clearAutoLeaveTimer, status])

  const handleLeaveMeeting = useCallback(() => {
    clearAutoLeaveTimer()

    if (isRedial) {
      patchReceptionCallHistory({
        caller_status: HISTORY_CALLER_STATUS.THE_CALL_FINISHED_NORMALLY,
      })
    } else {
      patchReceptionCallHistory({
        receiver_status: HISTORY_RECEIVER_STATUS.THE_CALL_FINISHED_NORMALLY,
      })
    }
    leaveMeeting()
  }, [clearAutoLeaveTimer, isRedial, leaveMeeting, patchReceptionCallHistory])

  useAttendeePresenceListener({
    selfId: attendee?.AttendeeId ?? '',
    onJoin: clearAutoLeaveTimer,
    onLeave: handleGuestLeave,
  })

  useEffect(() => {
    if (status !== 'calling') {
      clearAutoLeaveTimer()
    }
  }, [clearAutoLeaveTimer, status])

  useEffect(() => clearAutoLeaveTimer, [clearAutoLeaveTimer])

  useEffect(() => {
    if (status === 'callback') {
      playCallbackAudio()
    } else {
      stopCallbackAudio()
    }

    if (status === 'phoneRings' && reservation) {
      new Notification(`Incoming Call from ${information?.property}`, {
        body: `Language: ${t(information?.language ?? '')}`,
        tag: `incomingCall ${reservation.sid}`,
      })
      if (audioEnabled) {
        playRingAudio()
      }
    } else {
      stopRingAudio()
    }
  }, [
    status,
    playRingAudio,
    stopRingAudio,
    playCallbackAudio,
    stopCallbackAudio,
    audioEnabled,
    information,
    t,
    reservation,
  ])

  useEffect(() => {
    if (status === 'calling' && meeting && attendee) {
      if (!isRedial) {
        patchReceptionCallHistory({
          receiver_status: HISTORY_RECEIVER_STATUS.THE_CALL_ESTABLISHED,
        })
      } else {
        patchReceptionCallHistory({
          caller_status: HISTORY_CALLER_STATUS.THE_CALL_ESTABLISHED,
        })
      }
    }
  }, [status, patchReceptionCallHistory, meeting, attendee, isRedial])

  if (status === 'loading') {
    return <>Loading...</>
  }

  return (
    <>
      <Container styles={{ body: { height: '100%' } }}>
        <Flex>
          {status === 'waiting' && <Waiting>{t('waiting')}...</Waiting>}
          {(status === 'phoneRings' ||
            status === 'calling' ||
            status === 'callback') && (
            <Row>
              <Col>
                <MeetingInfo
                  isSmall={status === 'calling' || status === 'callback'}
                />
              </Col>
            </Row>
          )}
          {status === 'phoneRings' && (
            <Row style={{ height: '100%' }} justify={'center'} align={'middle'}>
              <Col span={24}>
                <Calling>
                  <Button
                    icon={<MdOutlineVideocam size={40} />}
                    onClick={async () => {
                      await accept()
                      await patchReceptionCallHistory({
                        receiver_status: HISTORY_RECEIVER_STATUS.RECEIVED,
                        received: new Date().toISOString(),
                      })
                    }}
                    block
                    type="primary"
                    style={{ height: '150px', fontSize: '28px' }}
                  >
                    {t('accept')}
                  </Button>
                </Calling>
              </Col>
            </Row>
          )}
          {(status === 'waiting' || status === 'phoneRings') && (
            <VolumeIcon
              enabled={audioEnabled}
              onClick={() => {
                setAudioEnabled(!audioEnabled)
                if (!audioEnabled) {
                  stopRingAudio()
                  stopCallbackAudio()
                }
              }}
            />
          )}
          {status === 'calling' && (
            <>
              <AutoLeaveCountdown
                durationMilliseconds={AUTO_LEAVE_TIMEOUT_MILLISECONDS}
                isActive={isAutoLeaveCountdownActive}
              />
              <Row
                style={{ flex: 1, minHeight: 0 }}
                justify={'center'}
                align={'middle'}
              >
                <Col span={18} style={{ height: '100%' }}>
                  <VideoContainer onLeaveMeeting={handleLeaveMeeting} />
                </Col>
                <Col span={6} style={{ height: '100%' }}>
                  <ChatComponent
                    messages={messages}
                    message={message}
                    setMessage={setMessage}
                    sendMessage={sendMessage}
                  />
                </Col>
              </Row>
            </>
          )}
          {status === 'callback' && (
            <Flex
              style={{
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Row>
                <Col span={24}>
                  <Title level={2} style={{ textAlign: 'center' }}>
                    {t('waitingForGuest')}
                  </Title>
                </Col>
              </Row>
              <Row style={{ marginTop: '20px' }}>
                <Col span={24}>
                  <Button
                    type="primary"
                    block
                    onClick={async () => {
                      await patchReceptionCallHistory({
                        caller_status: HISTORY_CALLER_STATUS.CANCEL,
                        canceled: new Date().toISOString(),
                      })
                      leaveMeeting()
                      // TODO: 後で直す
                      window.location.reload()
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Col>
              </Row>
            </Flex>
          )}
        </Flex>
      </Container>
      <Modal
        open={isAutoLeaveModalOpen}
        title={t('autoLeaveTitle')}
        closable={false}
        maskClosable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => window.location.reload()}
      >
        {t('autoLeaveDescription')}
      </Modal>
    </>
  )
}
